import { Alert, Button, Card, Input, Text } from '@ui/themed';
import { t } from 'i18next';
import type { I18nPrefix } from '../../../i18n/prefix';
import { useStorefrontOptionsControls } from '../context/storefront-options-context';
import type { CheckoutDropdownGroup, CheckoutDropdownOptionItem } from '../types/spark-options';
import { PerLocaleFieldsEditor } from './per-locale-fields-editor';
import { SortableList } from './sortable-list';

const EDITOR =
  'features:storefront_options.components.checkout_dropdown_options_editor' satisfies I18nPrefix;

function EmptyNotice({
  showError,
  message,
  errorMessage,
}: {
  showError: boolean;
  message: string;
  errorMessage: string;
}) {
  if (showError) {
    return (
      <Alert variant="error" headingLevel="h4">
        {errorMessage}
      </Alert>
    );
  }

  return (
    <Text size="sm" variant="secondary">
      {message}
    </Text>
  );
}

function ItemRow({
  item,
  onChange,
  onRemove,
}: {
  item: CheckoutDropdownOptionItem;
  onChange: (next: CheckoutDropdownOptionItem) => void;
  onRemove: () => void;
}) {
  const { showValidationErrors } = useStorefrontOptionsControls();
  const valueError = showValidationErrors && item.value.trim() === '';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <Input
            label={t(`${EDITOR}.option_value`)}
            placeholder={t(`${EDITOR}.option_value_placeholder`)}
            value={item.value}
            error={valueError ? t(`${EDITOR}.option_value_required`) : undefined}
            onChange={(value) => onChange({ ...item, value })}
          />
        </div>
        <Button
          variant="dangerBare"
          icon="delete"
          ariaLabel={t(`${EDITOR}.remove_option`)}
          onClick={onRemove}
        />
      </div>
      <PerLocaleFieldsEditor
        label={t(`${EDITOR}.option_label`)}
        value={item.translations}
        onChange={(next) => onChange({ ...item, translations: next })}
        namedFields={[
          {
            key: 'label',
            label: t(`${EDITOR}.option_label`),
            placeholder: t(`${EDITOR}.option_label_placeholder`),
            required: true,
          },
        ]}
        requireLocale
      />
    </div>
  );
}

function GroupCard({
  group,
  onChange,
  onRemove,
}: {
  group: CheckoutDropdownGroup;
  onChange: (next: CheckoutDropdownGroup) => void;
  onRemove: () => void;
}) {
  const { showValidationErrors } = useStorefrontOptionsControls();

  return (
    <Card size="sm">
      <Card.Header
        headingLevel="h3"
        primaryAction={{
          variant: 'dangerBare',
          icon: 'delete',
          ariaLabel: t(`${EDITOR}.remove_group`),
          onClick: onRemove,
        }}
      >
        {t(`${EDITOR}.group`)}
      </Card.Header>

      <Card.Body>
        <PerLocaleFieldsEditor
          label={t(`${EDITOR}.group_name`)}
          description={t(`${EDITOR}.group_name_hint`)}
          value={group.translations ?? []}
          onChange={(next) => onChange({ ...group, translations: next })}
          namedFields={[
            {
              key: 'groupName',
              label: t(`${EDITOR}.group_name`),
              placeholder: t(`${EDITOR}.group_name_placeholder`),
            },
          ]}
        />

        {group.items.length === 0 && (
          <EmptyNotice
            showError={showValidationErrors}
            message={t(`${EDITOR}.no_options`)}
            errorMessage={t(`${EDITOR}.no_options_error`)}
          />
        )}

        <SortableList
          items={group.items}
          getKey={(_, index) => `option-${index}`}
          onReorder={(items) => onChange({ ...group, items })}
          renderItem={(item, index) => (
            <ItemRow
              item={item}
              onChange={(next) =>
                onChange({ ...group, items: group.items.map((it, i) => (i === index ? next : it)) })
              }
              onRemove={() =>
                onChange({ ...group, items: group.items.filter((_, i) => i !== index) })
              }
            />
          )}
        />

        <div>
          <Button
            variant="secondary"
            size="xs"
            icon="plusCircle"
            onClick={() =>
              onChange({ ...group, items: [...group.items, { value: '', translations: [] }] })
            }
          >
            {t(`${EDITOR}.add_option`)}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}

export function CheckoutDropdownOptionsEditor({
  value,
  onChange,
}: {
  value: CheckoutDropdownGroup[];
  onChange: (value: CheckoutDropdownGroup[]) => void;
}) {
  const { showValidationErrors } = useStorefrontOptionsControls();

  return (
    <div className="flex flex-col gap-3">
      <Text size="sm">{t(`${EDITOR}.label`)}</Text>

      {value.length === 0 && (
        <EmptyNotice
          showError={showValidationErrors}
          message={t(`${EDITOR}.empty`)}
          errorMessage={t(`${EDITOR}.empty_error`)}
        />
      )}

      {value.map((group, index) => (
        <GroupCard
          // biome-ignore lint/suspicious/noArrayIndexKey: groups are positional and not reorderable
          key={index}
          group={group}
          onChange={(next) => onChange(value.map((g, i) => (i === index ? next : g)))}
          onRemove={() => onChange(value.filter((_, i) => i !== index))}
        />
      ))}

      <div>
        <Button
          variant="secondary"
          size="xs"
          icon="plusCircle"
          onClick={() => onChange([...value, { items: [] }])}
        >
          {t(`${EDITOR}.add_group`)}
        </Button>
      </div>
    </div>
  );
}
